import {
    ApplicationShell,
    FrontendApplication,
    FrontendApplicationContribution,
    KeybindingContribution,
    KeybindingRegistry,
    QuickInputService
} from '@theia/core/lib/browser';
import { ContextKeyService } from '@theia/core/lib/browser/context-key-service';
import { CommandHandler, CommandRegistry } from '@theia/core';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { quickCommand } from '@theia/core/lib/browser/quick-input/quick-command-service';
import {
    BrowserMainMenuFactory,
    DynamicMenuBarWidget,
    MenuBarWidget
} from '@theia/core/lib/browser/menu/browser-menu-plugin';
import { inject, injectable, optional } from '@theia/core/shared/inversify';
import { useUIStore } from '@lved/ui';
import {
    EDITOR_CHROME_LOCKED_CONTEXT_KEY,
    isCommandAllowedWhenEditorChromeLocked,
    setEditorChromeLockedBodyClass,
    syncAppShellInteractionLock
} from './editor-chrome-lock';

@injectable()
export class EditorChromeLockContribution implements FrontendApplicationContribution, KeybindingContribution {
    @inject(ContextKeyService)
    protected readonly contextKeys!: ContextKeyService;

    @inject(CommandRegistry)
    protected readonly commands!: CommandRegistry;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(BrowserMainMenuFactory)
    protected readonly menuFactory!: BrowserMainMenuFactory & {
        refreshMenuBar(menuBar: DynamicMenuBarWidget): void;
    };

    @inject(QuickInputService)
    @optional()
    protected readonly quickInputService?: QuickInputService;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();
    protected readonly blockedCommandIds: Set<string> = new Set();

    registerKeybindings(keybindings: KeybindingRegistry): void {
        for (const keybinding of ['f1', 'ctrlcmd+shift+p']) {
            keybindings.registerKeybinding({
                command: `-${quickCommand.id}`,
                keybinding,
                when: EDITOR_CHROME_LOCKED_CONTEXT_KEY
            });
        }
    }

    onStart(_app: FrontendApplication): void {
        const key = this.contextKeys.createKey<boolean>(EDITOR_CHROME_LOCKED_CONTEXT_KEY, false);

        const syncLockState = (locked: boolean): void => {
            key.set(locked);
            setEditorChromeLockedBodyClass(locked);
            syncAppShellInteractionLock();
            if (locked) {
                this.quickInputService?.hide();
            }
            this.refreshMenuBars();
        };

        const syncFromStore = (): void => {
            syncLockState(useUIStore.getState().editorChromeLocked);
        };

        syncFromStore();
        useUIStore.subscribe((state, previous) => {
            if (state.editorChromeLocked !== previous.editorChromeLocked) {
                syncLockState(state.editorChromeLocked);
            }
        });

        this.registerBlockedCommandHandlers();
        this.toDispose.push(this.commands.onCommandsChanged(() => this.registerBlockedCommandHandlers()));
    }

    onStop(_app: FrontendApplication): void {
        this.toDispose.dispose();
        setEditorChromeLockedBodyClass(false);
        syncAppShellInteractionLock();
    }

    protected registerBlockedCommandHandlers(): void {
        for (const commandId of this.commands.commandIds) {
            if (this.blockedCommandIds.has(commandId)) {
                continue;
            }
            this.blockedCommandIds.add(commandId);
            this.toDispose.push(this.commands.registerHandler(commandId, this.createBlockedCommandHandler(commandId)));
        }
    }

    protected createBlockedCommandHandler(commandId: string): CommandHandler {
        const shouldBlock = (args: unknown[]): boolean =>
            args.length === 0 &&
            useUIStore.getState().editorChromeLocked &&
            !isCommandAllowedWhenEditorChromeLocked(commandId);

        return {
            isVisible: (): false => false,
            isEnabled: (...args: unknown[]): boolean => shouldBlock(args),
            execute: (): undefined => undefined
        };
    }

    protected refreshMenuBars(): void {
        const factory = this.menuFactory as BrowserMainMenuFactory & {
            setMenuBar?: () => void;
            refreshMenuBar(menuBar: DynamicMenuBarWidget): void;
        };
        factory.setMenuBar?.();

        const menuBar = this.findMenuBarWidget();
        if (menuBar) {
            factory.refreshMenuBar(menuBar);
        }
    }

    protected findMenuBarWidget(): DynamicMenuBarWidget | undefined {
        return this.shell.topPanel.widgets.find(
            (widget): widget is DynamicMenuBarWidget => widget instanceof MenuBarWidget
        );
    }
}
